import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Place} from '../types/Place';
import {Section} from '../types/Section';
import placesService from '../services/placesService';
import imagesService from '../services/imagesService';
import {handleError} from '../../../common/utils/errorHandling';
import {FileUpload} from 'primereact/fileupload';
import {useNavigate, useParams} from 'react-router-dom';
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {
  AdminButton,
  AdminForm,
  AdminFormSection,
  AdminInputDropdown,
  AdminInputText,
  FieldLanguageSwitcher
} from "../../../layout/admin";
import AdminImagesSection from "../../../layout/admin/AdminImagesSection";
import {DictionaryItemListing, DictionaryType} from '../../core/types/DictionaryItem';
import dictionaryItemsService from '../../core/services/dictionaryItemsService';
import AdminTextSections from "../../../layout/admin/AdminTextSections";
import {HierarchyType, OrganizationalUnit} from '../types/OrganizationalUnit';
import organizationalUnitsService from '../services/organizationalUnitsService';
import AddressFormSection from './AddressFormSection';
import {Publication} from '../../publications/types/Publication';
import publicationsService from '../../publications/services/publicationsService';
import {useTranslatableField} from '../../../common/hooks/useTranslatableField';
import {getTranslatedLanguages, StringTranslations} from '../../../common/types/StringTranslations';
import {useFormErrorHandling} from "../../../common/hooks/useFormErrorHandling";

const PlaceForm: React.FC = () => {
  const {t} = useTranslation();
  const nameField = useTranslatableField();
  const {errors, handleApiError, clearErrors} = useFormErrorHandling();

  const [formData, setFormData] = useState({
    churchUnitId: '',
    publicationId: '',
    street: '',
    streetNumber: '',
    apartmentNumber: '',
    town: '',
    postalCode: '',
    administrationUnitId: '',
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined
  });

  const {id} = useParams<{ id: string }>();
  const [place, setPlace] = useState<Place | undefined>();
  const [sectionTypes, setSectionTypes] = useState<DictionaryItemListing[]>([]);
  const [sectionContent, setSectionContent] = useState<Map<string, StringTranslations>>(new Map());
  const [churchUnits, setChurchUnits] = useState<OrganizationalUnit[]>([]);
  const [administrationUnits, setAdministrationUnits] = useState<OrganizationalUnit[]>([]);
  const [publications, setPublications] = useState<Publication[]>([]);
  const navigate = useNavigate();
  const fileUploadRef = useRef<FileUpload>(null);

  useEffect(() => {
    fetchSectionTypes();
    fetchChurchUnits();
    fetchAdministrationUnits();
    fetchPublications();
    if (id) {
      fetchPlace(id);
    }
  }, [id]);

  const fetchSectionTypes = async (): Promise<void> => {
    await handleError(
      async () => {
        const types = await dictionaryItemsService.getDictionaryItemsByType(DictionaryType.PLACE_SECTION);
        setSectionTypes(types);
      },
      t('admin.places.errors.fetchingSectionTypes')
    );
  };

  const fetchChurchUnits = async (): Promise<void> => {
    await handleError(
      async () => {
        const units = await organizationalUnitsService.getOrganizationalUnitsByHierarchyType(HierarchyType.CHURCH);
        setChurchUnits(units);
      },
      t('admin.places.errors.fetchingChurchUnits')
    );
  };

  const fetchAdministrationUnits = async (): Promise<void> => {
    await handleError(
      async () => {
        const units = await organizationalUnitsService.getOrganizationalUnitsByHierarchyType(HierarchyType.ADMINISTRATIVE);
        setAdministrationUnits(units);
      },
      t('admin.places.errors.fetchingAdminUnits')
    );
  };

  const fetchPublications = async (): Promise<void> => {
    await handleError(
      async () => {
        const pubs = await publicationsService.getAllPublications();
        setPublications(pubs);
      },
      t('admin.places.errors.fetchingPublications')
    );
  };

  const fetchPlace = async (placeId: string): Promise<void> => {
    await handleError(
      async () => {
        const placeData = await placesService.getPlace(placeId);
        setPlace(placeData);
        nameField.setTranslations(placeData.name);
        setFormData({
          churchUnitId: placeData.churchUnit?.id || '',
          publicationId: placeData.publication?.id || '',
          street: placeData.address?.street || '',
          streetNumber: placeData.address?.streetNumber || '',
          apartmentNumber: placeData.address?.apartmentNumber || '',
          town: placeData.address?.town || '',
          postalCode: placeData.address?.postalCode || '',
          administrationUnitId: placeData.address?.administrationUnit?.id || '',
          latitude: placeData.address?.latitude,
          longitude: placeData.address?.longitude
        });
        initializeSectionContent(placeData.sections || []);
      },
      t('admin.places.errors.fetchingPlace')
    )
  };

  const initializeSectionContent = (sections: Section[]): void => {
    const contentMap = new Map<string, StringTranslations>();

    // Group sections by type and build StringTranslations
    sections.forEach(section => {
      const typeAlias = section.type.alias;
      const existingTranslations = contentMap.get(typeAlias) || {};
      const updatedTranslations = {
        ...existingTranslations,
        [section.language]: section.content
      };
      contentMap.set(typeAlias, updatedTranslations);
    });

    setSectionContent(contentMap);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {name, value} = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSectionContentChange = (typeAlias: string, translations: StringTranslations): void => {
    setSectionContent(prev => new Map(prev).set(typeAlias, translations));
  };

  const handleCoordinatesChange = (latitude?: number, longitude?: number): void => {
    setFormData(prev => ({
      ...prev,
      latitude,
      longitude
    }));
  };

  const buildSectionsForSubmission = (): Section[] => {
    const sections: Section[] = [];

    sectionContent.forEach((translations, typeAlias) => {
      const translatedLanguages = getTranslatedLanguages(translations);

      translatedLanguages.forEach(language => {
        const content = translations[language];
        if (content && content.trim()) {
          // Find existing section with same type and language
          const existingSection = place?.sections?.find(
            s => s.type.alias === typeAlias && s.language === language
          );

          sections.push({
            id: existingSection?.id || crypto.randomUUID(),
            type: sectionTypes.find(t => t.alias === typeAlias)!,
            content: content.trim(),
            language: language
          });
        }
      });
    });

    return sections;
  };

  const buildChurchUnit = () => {
    if (!formData.churchUnitId) return undefined;
    const unit = churchUnits.find(u => u.id === formData.churchUnitId);
    return unit ? {id: unit.id, name: unit.name, type: unit.type} : undefined;
  };

  const buildPublication = () => {
    if (!formData.publicationId) return undefined;
    const pub = publications.find(p => p.id === formData.publicationId);
    return pub ? {id: pub.id, name: pub.name} : undefined;
  };

  const buildAddress = () => {
    const hasAddressData = formData.street || formData.streetNumber ||
      formData.apartmentNumber || formData.town ||
      formData.postalCode || formData.administrationUnitId ||
      formData.latitude !== undefined || formData.longitude !== undefined;
    if (!hasAddressData) return undefined;

    const administrationUnit = formData.administrationUnitId ?
      administrationUnits.find(u => u.id === formData.administrationUnitId) : undefined;

    return {
      id: place?.address?.id || crypto.randomUUID(),
      street: formData.street || undefined,
      streetNumber: formData.streetNumber || undefined,
      apartmentNumber: formData.apartmentNumber || undefined,
      town: formData.town || undefined,
      postalCode: formData.postalCode || undefined,
      administrationUnit: administrationUnit ?
        {id: administrationUnit.id, name: administrationUnit.name, type: administrationUnit.type} :
        undefined,
      latitude: formData.latitude,
      longitude: formData.longitude
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();

    try {
      const sections = buildSectionsForSubmission();
      const churchUnit = buildChurchUnit();
      const address = buildAddress();
      const publication = buildPublication();

      if (place) {
        await placesService.updatePlace({
          ...place,
          name: nameField.translations,
          churchUnit,
          address,
          publication,
          sections
        });
      } else {
        await placesService.createPlace({
          name: nameField.translations,
          churchUnit,
          address,
          publication,
          sections
        });
      }
      navigate('/places');
    } catch (error) {
      handleApiError(error);
    }
  };

  const churchUnitOptions = useMemo(
    () => churchUnits.map(unit => ({
      label: `${unit.name} (${unit.type})`,
      value: unit.id
    })),
    [churchUnits]
  );

  const publicationOptions = useMemo(
    () => publications.map(pub => ({
      label: pub.name,
      value: pub.id
    })),
    [publications]
  );

  const handlePreviewClick = () => {
    if (id) {
      window.open(`/gallery/place/${id}`, '_blank');
    }
  };

  return (
    <>
      <AdminFormHeader
        title={place ? t('admin.places.title.edit') : t('admin.places.title.create')}
        subtitle={t('admin.places.subtitle.form')}
        onPreviewClick={id ? handlePreviewClick : undefined}
      />

      <AdminForm
        leftColumn={
          <>
            <AdminFormSection
              title={t('admin.common.sections.basicInfo')}
            >
              <AdminInputText
                label={t('admin.common.fields.name')}
                value={nameField.currentValue}
                onChange={(e) => nameField.setCurrentValue(e.target.value)}
                name="name"
                id="name"
                required={true}
                languageSwitcher={
                  <FieldLanguageSwitcher
                    currentLanguage={nameField.currentLanguage}
                    onLanguageChange={nameField.setCurrentLanguage}
                    collapsed={true}
                    translatedLanguages={nameField.translatedLanguages}
                  />
                }
                error={errors.name}
              />
            </AdminFormSection>

            <AdminFormSection
              title={t('admin.places.sections.location')}
            >
              <AdminInputDropdown
                label={t('admin.places.fields.churchUnit')}
                value={formData.churchUnitId}
                onChange={handleInputChange}
                options={churchUnitOptions}
                placeholder={t('admin.places.placeholders.selectChurchUnit')}
                name="churchUnitId"
                id="churchUnitId"
                error={errors['churchUnit.id']}
              />
              <AddressFormSection
                formData={{
                  street: formData.street,
                  streetNumber: formData.streetNumber,
                  apartmentNumber: formData.apartmentNumber,
                  town: formData.town,
                  postalCode: formData.postalCode,
                  administrationUnitId: formData.administrationUnitId,
                  latitude: formData.latitude,
                  longitude: formData.longitude
                }}
                administrationUnits={administrationUnits}
                onChange={handleInputChange}
                onCoordinatesChange={handleCoordinatesChange}
                errors={{
                  street: errors['address.street'],
                  streetNumber: errors['address.streetNumber'],
                  apartmentNumber: errors['address.apartmentNumber'],
                  town: errors['address.town'],
                  postalCode: errors['address.postalCode'],
                  administrationUnitId: errors['address.administrationUnit.id']
                }}
              />
            </AdminFormSection>

            <AdminFormSection
              title={t('admin.places.sections.publication')}
            >
              <AdminInputDropdown
                label={t('admin.places.fields.publication')}
                value={formData.publicationId}
                onChange={handleInputChange}
                options={publicationOptions}
                placeholder={t('admin.places.placeholders.selectPublication')}
                name="publicationId"
                id="publicationId"
                error={errors['publication.id']}
              />
            </AdminFormSection>

            <AdminTextSections
              title={t('admin.common.sections.content')}
              sectionsContent={sectionContent}
              sectionTypes={
                ['TSKP', 'PPLK'].map(alias => sectionTypes.find(t => t.alias === alias)).filter(Boolean) as DictionaryItemListing[]
              }
              handleSectionContentChange={handleSectionContentChange}
            />

          </>
        }
        rightColumn={
          <>
            {place && (
              <AdminImagesSection
                images={place?.images || []}
                fileUploadRef={fileUploadRef}
                objectId={place.id}
                fetchObject={(placeId) => fetchPlace(placeId)}
                uploadImage={imagesService.uploadImageForPlace}
                deleteImage={imagesService.deletePlaceImage}
                reorderImages={imagesService.reorderPlaceImages}
              />
            )}
          </>
        }
        formActions={
          <>
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={() => navigate("/places")}
            />
            <AdminButton
              type="submit"
              label={place ? t('admin.common.actions.update') : t('admin.common.actions.create')}
              variant="black"
            />
          </>
        }
        onSubmit={handleSubmit}
      />

    </>
  );
};

export default PlaceForm;
